import { Button } from "@mui/material";
import dayjs from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";

import Page from "~/components/Page";
import AdminBillingExportTable from "~/tables/AdminBillingExportTable";
import TableControls from "~/components/TableControls";
import { useTrans } from "~/hooks/useTrans";
import { apiGetBillingExport } from "~/providers/api";
import ErrorInfo from "~/components/ErrorInfo";
import { myUseState } from "~/hooks/myUseState";
import { useLang } from "~/hooks/useLang";

import "./style.less";
import { downloadBlob } from "~/utils/utils";
import BillingExportSelect, { TExportType } from "~/components/BillingExportSelect";

interface IState {
	loading: boolean;
	isDirty: boolean;
	errorMsg: string;
	exportDate: dayjs.Dayjs;
	exportType: TExportType;
}

const INIT_STATE: IState = {
	loading: false,
	isDirty: false,
	errorMsg: "",
	exportDate: dayjs().subtract(1, "month").startOf("month"),
	exportType: "invoice",
};

export default function AdminBillingExportPage() {
	const { trans } = useTrans();
	const { lang } = useLang();

	function getInitState(): IState {
		return {
			...INIT_STATE,
		};
	}

	const { state, updateState, setState } = myUseState<IState>(getInitState());

	async function loadData() {
		updateState({
			loading: true,
			errorMsg: "",
		});

		const responseData = await apiGetBillingExport(
			state.exportType,
			state.exportDate.format("YYYY-MM-DD"),
			lang,
		);

		updateState({
			loading: false,
			errorMsg: responseData.errors.join(", "),
		});

		/* eslint-disable-next-line */
		if (responseData.status === 200) {
			downloadBlob(new Blob([responseData.data]), responseData.fileName);
		}
	}

	function updateExportDate(value: Date) {
		setState({
			...state,
			exportDate: dayjs(value),
		});
	}

	function downloadBillingExport() {
		loadData();
	}

	function onPageLoad(isAuthorized: boolean) {
		if (isAuthorized) {
			setState(getInitState());
		}
	}

	return (
		<LocalizationProvider dateAdapter={AdapterDayjs}>
			<Page
				title="Billing Export"
				breadcrumbs={[trans("administration"), "Billing Export"]}
				checkInternalRole="salesman"
				onLoad={onPageLoad}
				isDirty={state.isDirty}
				seoTitleTrans="seo.title.admin.transactions"
			>
				<TableControls className="adminServicesPage__tableControls">
					<div className="adminBillingExportPage__tableControlsLine">
						<BillingExportSelect selected={state.exportType} onChange={selected => updateState({ exportType: selected })}/>
						<DateCalendar
							value={state.exportDate}
							onChange={updateExportDate}
							views={["month", "year"]}
							openTo="month"
						/>
						<Button
							className="adminBillingExportPage__tableControlsLine--button"
							color="success"
							variant="contained"
							onClick={downloadBillingExport}
						>
							Download
						</Button>
					</div>
				</TableControls>
				<AdminBillingExportTable loading={state.loading} />
				{state.errorMsg && (
					<ErrorInfo
						msg={state.errorMsg}
						className="margin-top-large"
					/>
				)}
			</Page>
		</LocalizationProvider>
	);
}
